import React from 'react'
import type {CSSProperties} from 'react'
import {PromotionPills} from '../../store/PromotionStore'
import {Flex} from '../../vanilla'
import {useIntl} from 'react-intl'
import {useGlobalStore} from '../../store/hooks/useStore'
import {promoPill} from './styles.css'

export type pillType = PromotionPills | null | undefined

type PillConfig = {
  text: string
  color?: 'midYellows' | 'lightYellows' | 'default'
}

const pillRanking: Record<string, number> = {
  savings: 1,
  servingSize: 2,
  segment: 3
}

const pillMapping: Record<string, PillConfig> = {
  savings: {
    text: 'Save up to ',
    color: 'midYellows'
  },
  actualSavings: {
    text: "You've saved ",
    color: 'lightYellows'
  },
  servingSize: {
    text: 'Serves '
  },
  segment: {
    text: ''
  }
}

type PromoPillsStyling = Omit<CSSProperties, 'background' | 'borderColor'> & {
  background?: string
  borderColor?: string
}

type PromoPillsProps = {
  pills: pillType
  styling?: PromoPillsStyling
}

function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const PromoPills = ({pills, styling = {}}: PromoPillsProps) => {
  const {formatNumber} = useIntl()
  const {currency} = useGlobalStore()
  const {background, borderColor, ...flexStyling} = styling
  const validPills =
    pills && typeof pills === 'object' && !Array.isArray(pills)
      ? Object.entries(pills).filter(
          ([, value]) => value !== undefined && value !== null && value !== ''
        )
      : []

  if (validPills.length === 0) {
    return null
  }

  return (
    <Flex
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
      marginBottom="8px"
      gap="10px"
      flexWrap="wrap"
      style={flexStyling}
    >
      {validPills
        .sort(([keyA], [keyB]) => {
          const rankA = pillRanking[keyA] ?? 999
          const rankB = pillRanking[keyB] ?? 999
          return rankA - rankB
        })
        .map(([key, value]) => {
          const mapping = pillMapping[key]
          const values =
            key === 'segment' ? String(value).split(',').map((item) => item.trim()) : [value]

          return values.map((pillValue, index) => {
            const pillMeta = {
              text: mapping?.text ?? `${capitalizeFirstLetter(key)} `,
              color: mapping?.color ?? 'default'
            }

            return (
              <Flex
                key={`${key}-${index}`}
                className={promoPill({weight: 'bold', colors: pillMeta.color})}
                data-test-selector={`meal-deal-pill-${key}`}
                color="gray800"
                style={
                  pillMeta.color === 'default' && (background || borderColor)
                    ? {backgroundColor: background, borderColor}
                    : undefined
                }
              >
                <span>
                  {pillMeta.text}{' '}
                  {key === 'savings' || key === 'actualSavings'
                    ? formatNumber(Number(String(pillValue)), {style: 'currency', currency})
                    : String(pillValue)}
                </span>
              </Flex>
            )
          })
        })}
    </Flex>
  )
}

export const CalculatedPromoPills = ({dealCost, actualCost, mealDealQuantity = 1}: {dealCost: number; actualCost: number; mealDealQuantity: number}) => {
  const savings = (actualCost - dealCost) * mealDealQuantity
  if (savings < 0.5) return null
  return PromoPills({pills: {actualSavings: savings}})
}
